import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { LinkOffOutlined } from '@mui/icons-material';
import {
  ProductFlowLink,
  ProductFlowsDataSource,
} from '../../../../datasources/productFlows/ProductFlowsDataSource';
import { FlowsDataSource } from '../../../../datasources/flows/FlowsDataSource';
import {
  getApiErrorMessage,
  getGenericErrorMessage,
} from '../../../../datasources/GenericErrors';
import { Flow } from '../../../../types/flow/Flow';
import { ProductBasket } from '../../../../types/basket/ProductBasket';
import { Modal } from '../../../../shared/ui/Modal';
import { Footer } from '../../../../shared/ui/Modal/Body';
import { SelectProduct } from '../basket/products/selectProduct';
import { BasketListScreen } from '../shared/BasketListScreen';
import { BasketTable, BasketTableColumn } from '../shared/BasketTable';
import { useTextSearch } from '../shared/useTextSearch';

/**
 * Liga produtos a um fluxo (basket_product_flow). Na implantação, o produto
 * vinculado abre o questionário desse fluxo; sem vínculo, segue o fluxo antigo.
 */
export const ProductFlowPage = () => {
  const [links, setLinks] = useState<ProductFlowLink[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [retry, setRetry] = useState(0);
  const [busySapCode, setBusySapCode] = useState<string>();
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([ProductFlowsDataSource.getAll(), FlowsDataSource.getAll()])
      .then(([linkData, flowData]) => {
        if (cancelled) return;
        setLinks(linkData);
        setFlows(flowData);
        setError(undefined);
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(
            getGenericErrorMessage(
              loadError,
              'Não foi possível carregar os vínculos de produtos.'
            )
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [retry]);

  async function changeFlow(link: ProductFlowLink, flowId: number): Promise<void> {
    setBusySapCode(link.product_sap_code);
    try {
      setLinks(await ProductFlowsDataSource.link(flowId, [link.product_sap_code]));
      setError(undefined);
    } catch (changeError) {
      setError(getApiErrorMessage(changeError, 'Não foi possível trocar o fluxo.'));
    } finally {
      setBusySapCode(undefined);
    }
  }

  async function unlink(link: ProductFlowLink): Promise<void> {
    const name = link.product?.description ?? link.product_sap_code;
    if (!window.confirm(`Remover o vínculo do produto "${name}"?`)) return;

    setBusySapCode(link.product_sap_code);
    try {
      await ProductFlowsDataSource.unlink(link.product_sap_code);
      setLinks((current) =>
        current.filter((item) => item.product_sap_code !== link.product_sap_code)
      );
      setError(undefined);
    } catch (unlinkError) {
      setError(getApiErrorMessage(unlinkError, 'Não foi possível remover o vínculo.'));
    } finally {
      setBusySapCode(undefined);
    }
  }

  const columns: BasketTableColumn<ProductFlowLink>[] = [
    {
      id: 'sap',
      label: 'Código SAP',
      minWidth: 120,
      render: (link) => link.product_sap_code,
    },
    {
      id: 'product',
      label: 'Produto',
      minWidth: 280,
      render: (link) =>
        link.product?.description ?? (
          <Typography variant="body2" color="error">
            Produto não encontrado no catálogo
          </Typography>
        ),
    },
    {
      id: 'flow',
      label: 'Fluxo',
      minWidth: 240,
      render: (link) => {
        const flowMissing = !link.flow || !link.flow.active;
        return (
          <TextField
            select
            size="small"
            fullWidth
            value={flowMissing ? '' : link.basket_flow_id}
            onChange={(event) => void changeFlow(link, Number(event.target.value))}
            disabled={busySapCode === link.product_sap_code}
            error={flowMissing}
            helperText={flowMissing ? 'Fluxo excluído ou inativo' : undefined}
          >
            {flows.map((flow) => (
              <MenuItem key={flow.id} value={flow.id}>
                {flow.code}
              </MenuItem>
            ))}
          </TextField>
        );
      },
    },
  ];

  return (
    <>
      <BasketListScreen
        title="Produtos"
        description="Vincule produtos a um fluxo. Na implantação, o produto vinculado abre as perguntas do fluxo."
        actionLabel="Vincular produtos"
        onAction={() => setDialogOpen(true)}
        actionDisabled={flows.length === 0}
        searchPlaceholder="Buscar por produto, código SAP ou fluxo..."
        loading={loading}
        loadingLabel="Carregando vínculos"
        error={error}
        onRetry={() => setRetry((attempt) => attempt + 1)}
        retryLabel="Recarregar"
        notice={
          flows.length === 0 && (
            <Alert severity="info">
              Crie um fluxo na aba Fluxos antes de vincular produtos.
            </Alert>
          )
        }
      >
        {(search) => (
          <ProductLinksTable
            links={links}
            columns={columns}
            search={search}
            busySapCode={busySapCode}
            onUnlink={(link) => void unlink(link)}
          />
        )}
      </BasketListScreen>

      <LinkProductsDialog
        open={dialogOpen}
        flows={flows}
        links={links}
        onClose={() => setDialogOpen(false)}
        onSaved={(updated) => {
          setLinks(updated);
          setError(undefined);
          setDialogOpen(false);
        }}
      />
    </>
  );
};

type ProductLinksTableProps = {
  links: ProductFlowLink[];
  columns: BasketTableColumn<ProductFlowLink>[];
  search: string;
  busySapCode?: string;
  onUnlink: (link: ProductFlowLink) => void;
};

const ProductLinksTable = ({
  links,
  columns,
  search,
  busySapCode,
  onUnlink,
}: ProductLinksTableProps) => {
  const rows = useTextSearch(links, search, (link) => [
    link.product_sap_code,
    link.product?.description,
    link.flow?.code,
  ]);

  return (
    <BasketTable
      columns={columns}
      rows={rows}
      getRowId={(link) => link.product_sap_code}
      getRowLabel={(link) => `produto ${link.product_sap_code}`}
      itemsLabel="produtos"
      emptyMessage="Nenhum produto vinculado. Na implantação, esses produtos seguem o fluxo antigo."
      searching={search.trim() !== ''}
      resetKey={search}
      menuActions={[
        {
          label: 'Remover vínculo',
          icon: <LinkOffOutlined fontSize="small" />,
          destructive: true,
          disabled: (link) => busySapCode === link.product_sap_code,
          onClick: onUnlink,
        },
      ]}
    />
  );
};

type LinkProductsDialogProps = {
  open: boolean;
  flows: Flow[];
  links: ProductFlowLink[];
  onClose: () => void;
  onSaved: (links: ProductFlowLink[]) => void;
};

const LinkProductsDialog = ({ open, flows, links, onClose, onSaved }: LinkProductsDialogProps) => {
  const [flowId, setFlowId] = useState<number | ''>('');
  const [products, setProducts] = useState<ProductBasket[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setFlowId('');
    setProducts([]);
    setError(undefined);
  }, [open]);

  const linkBySap = new Map(links.map((link) => [link.product_sap_code, link]));
  const replaced = products.filter((product) => {
    const current = linkBySap.get(product.sap_code.trim());
    return current && current.basket_flow_id !== flowId;
  });

  async function save(): Promise<void> {
    if (flowId === '' || products.length === 0) return;
    setSaving(true);
    try {
      onSaved(
        await ProductFlowsDataSource.link(
          flowId,
          products.map((product) => product.sap_code)
        )
      );
    } catch (saveError) {
      setError(getApiErrorMessage(saveError, 'Não foi possível vincular os produtos.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} handleClose={() => !saving && onClose()} width="min(640px, 95vw)">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#444' }}>Vincular produtos a um fluxo</span>

        <TextField
          select
          size="small"
          label="Fluxo"
          value={flowId}
          onChange={(event) => setFlowId(Number(event.target.value))}
          disabled={saving}
        >
          {flows.map((flow) => (
            <MenuItem key={flow.id} value={flow.id}>
              {flow.code}
            </MenuItem>
          ))}
        </TextField>

        <div>
          <Button variant="outlined" onClick={() => setPickerOpen(true)} disabled={saving}>
            Buscar produtos
          </Button>
        </div>

        {products.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum produto escolhido.
          </Typography>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {products.map((product) => (
              <Chip
                key={product.id}
                label={`${product.sap_code} - ${product.description}`}
                onDelete={() =>
                  setProducts((current) => current.filter((item) => item.id !== product.id))
                }
                disabled={saving}
              />
            ))}
          </div>
        )}

        {replaced.length > 0 && flowId !== '' && (
          <Alert severity="warning">
            {replaced.length === 1 ? 'Este produto já tem' : 'Estes produtos já têm'} outro fluxo, que
            será trocado: {replaced.map((product) => product.sap_code).join(', ')}.
          </Alert>
        )}

        {error && <Alert severity="error">{error}</Alert>}
      </div>

      <Footer>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={saving || flowId === '' || products.length === 0}
        >
          {saving ? 'Salvando...' : 'Vincular'}
        </Button>
      </Footer>

      <SelectProduct
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        existingProducts={products}
        onConfirm={(selected) =>
          setProducts((current) => {
            const ids = new Set(current.map((product) => product.id));
            return [...current, ...selected.filter((product) => !ids.has(product.id))];
          })
        }
      />
    </Modal>
  );
};
